import {
  useEmployeeEnrollmentInfoQuery,
  useMutationNotifier,
  useTrackingContext,
} from '@alice-financial/alice-sdk'
import {
  ActionLayout,
  birthdateMinMax,
  Button,
  CheckboxController,
  Container,
  DateInputController,
  EmailInputController,
  ExternalLink,
  Fieldset,
  FormLabel,
  Grid,
  Legend,
  TelFieldController,
  TextFieldController,
  Typography,
} from '@alice-financial/pretext-ui'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useParams } from 'react-router'
import { ServerError } from '../../utils/forms/ServerError'
import { AddressInput } from '../employeeDashboard/profile/address/AddressInput'
import { PageBody } from '../routes/PageBody'
import { VerifyPhoneDialog } from '../user/confirmSMS/VerifyPhoneDialog'
import { useRequestPhoneVerification } from '../user/useRequestPhoneVerification'
import { AliceCardTosLabel } from './AliceCardTosLabel'
import { NonEditableRegisterInput } from './types'
import { useRegister } from './useRegister'
import { UseRegisterEmployeeOptions } from './useRegisterEmployee'

const useReferredBy = () => {
  const { referredById } = useParams()
  const [{ utmParams }] = useTrackingContext()

  if (referredById) return parseInt(referredById, 10)
  if (utmParams?.utm_source === 'referral' && utmParams?.utm_term) {
    return parseInt(utmParams.utm_term, 10)
  }
  return undefined
}

/**
 * Initial registration page for a user. In general, we cannot assume that the user
 * is logged in at this point, so we need to choose between a userUpdate call
 * and a createUser call.
 *
 * Currently, if you call `createUser` with existing user information, the user
 * will not be updated but the endpoint will return their existing information.
 *
 * ??? Are you wondering how the user navigates to the next step? Check out
 * `EnrollmentRouter`, which will watch for `user` state changes (using `useEnrollmentStatus`)
 * and redirect the user to the appropriate page.
 *
 * _Routing/navigation should not be defined in this component_.
 */
export const Register = () => {
  const intl = useIntl()
  const { data: enrollmentData, isLoading: isLoadingEnrollmentData } = useEmployeeEnrollmentInfoQuery()
  const user = enrollmentData?.currentUser
  const employee = enrollmentData?.employee
  const hasAddress = Boolean(employee?.mailingAddress?.line1)

  const { enrollmentCode } = useParams()
  const referredBy = useReferredBy()
  if (!enrollmentCode) throw new Error('URL must contain enrollment code as a param')

  const nonEditableInput: NonEditableRegisterInput = {
    inviteCode: enrollmentCode,
    referredBy,
  }

  // by default, we allow the phone verification dialog to be mounted, which will handle returning users
  // who have completed the registration form but not yet verified their phone number.
  // However, we allow the user to close the dialog and return to the registration form in case they
  // want to edit their information and re-submit before verifying their phone number.
  const [mountVerifyPhoneDialog, setMountVerifyPhoneDialog] = React.useState(true)
  const onCloseVerifyPhoneDialog = () => setMountVerifyPhoneDialog(false)
  const { mutate: sendPhoneVerificationCode } = useRequestPhoneVerification()
  const registerOptions: UseRegisterEmployeeOptions = useMutationNotifier(
    {
      onMutate: intl.formatMessage({ id: 'enroll.register.updating_your_info' }),
    },
    {
      onSuccess: (data) => {
        setMountVerifyPhoneDialog(true)
        const tel = data.registerEmployee?.employee?.user.tel
        if (!tel || tel?.isSmsConfirmed || tel?.isConfirmationCodeSent) return
        sendPhoneVerificationCode({ tel: tel.nationalNumber })
      },
    }
  )
  // receive the created user on success
  const { onSubmit, control, formState, isLoading } = useRegister(nonEditableInput, registerOptions)
  const submitDisabled = isLoading || isLoadingEnrollmentData

  return (
    <PageBody>
      <Container>
        <Typography variant="h1" gutterBottom={Boolean(user)}>
          <FormattedMessage id="enroll.register.greeting" />
        </Typography>
        {!user && (
          <Typography variant="body2" gutterBottom>
            <FormattedMessage id="enroll.register.if_account_then_sign_in" />
          </Typography>
        )}

        <form data-testid="register-user" onSubmit={onSubmit}>
          <Fieldset style={{ marginBottom: '1em' }}>
            <Legend gutterBottom>
              <FormattedMessage id="enroll.register.personal_info.heading" />
            </Legend>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextFieldController
                  control={control}
                  name="firstName"
                  autoComplete="given-name"
                  label={intl.formatMessage({ id: 'profile.first_name_label' })}
                  required
                  fullWidth
                  rules={{
                    required: intl.formatMessage({ id: 'profile.name.validation.required' }),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextFieldController
                  control={control}
                  name="lastName"
                  autoComplete="family-name"
                  label={intl.formatMessage({ id: 'profile.last_name_label' })}
                  required
                  fullWidth
                  rules={{
                    required: intl.formatMessage({ id: 'profile.name.validation.required' }),
                  }}
                />
              </Grid>

              {!user?.birthdate && (
                <Grid size={{ xs: 12, sm: 8 }}>
                  <DateInputController
                    name="birthdate"
                    min={birthdateMinMax.min}
                    max={birthdateMinMax.max}
                    required
                    label={intl.formatMessage({ id: 'profile.birthdate.label' })}
                    control={control}
                  />
                </Grid>
              )}
            </Grid>
          </Fieldset>
          <Fieldset style={{ marginBottom: '1em' }}>
            <Legend gutterBottom>
              <FormattedMessage id="enroll.register.contact_info.heading" />
            </Legend>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 8 }}>
                <EmailInputController
                  name="email"
                  label={intl.formatMessage({ id: 'profile.email_label' })}
                  required
                  fullWidth
                  helperText={intl.formatMessage({ id: 'enroll.register.email_address_helper' })}
                  autoComplete="email"
                  control={control}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 8 }}>
                <TelFieldController
                  name="tel"
                  label={intl.formatMessage({ id: 'profile.tel_label' })}
                  required
                  fullWidth
                  helperText={
                    !user?.tel?.isSmsConfirmed &&
                    intl.formatMessage({ id: 'enroll.register.phone_number_helper' })
                  }
                  autoComplete="tel"
                  control={control}
                />
              </Grid>
              {!hasAddress && (
                <Grid size={{ xs: 12, sm: 8 }}>
                  <FormLabel sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <FormattedMessage id="common.mailing_address" />
                  </FormLabel>
                  <AddressInput control={control} />
                </Grid>
              )}
            </Grid>
          </Fieldset>
          <Fieldset style={{ marginBottom: '1em' }}>
            <Legend gutterBottom>
              <FormattedMessage id="enroll.register.agreements.heading" />
              <br />
              <Typography variant="body2">
                <FormattedMessage id="enroll.register.agreements.subheading" />
              </Typography>
            </Legend>
            <CheckboxController
              color="primary"
              name="acceptTermsAndEsign"
              control={control}
              rules={{
                validate: (v) =>
                  v === true || intl.formatMessage({ id: 'enroll.register.validation.require_to_read_docs' }),
              }}
              label={
                <Typography variant="body2">
                  <FormattedMessage
                    id="enroll.register.legal_agreement.i_have_read_and_agree"
                    values={{
                      'esign-consent': () => (
                        <ExternalLink target="_blank" href="/docs/esign-consent">
                          <FormattedMessage id="enroll.register.legal_agreement.esign_disclosure_and_consent" />
                        </ExternalLink>
                      ),
                      'terms-and-conditions': () => (
                        <ExternalLink target="_blank" href="/docs/tos-employee">
                          <FormattedMessage id="enroll.register.legal_agreement.terms_and_conditions" />.
                        </ExternalLink>
                      ),
                    }}
                  />
                </Typography>
              }
            />

            <CheckboxController
              color="primary"
              name="acceptAliceCardTos"
              control={control}
              rules={{
                validate: (v) =>
                  v === true || intl.formatMessage({ id: 'cards.alice.confirm_you_read_the_documents' }),
              }}
              label={<AliceCardTosLabel />}
            />
          </Fieldset>
          <ServerError errors={formState.errors} />
          <ActionLayout
            primary={
              <div>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  color="primary"
                  type="submit"
                  disabled={submitDisabled}
                >
                  <FormattedMessage id={user ? 'enroll.register.confirm_info_label' : 'common.continue'} />
                </Button>
                {Object.keys(formState.errors).length > 0 && (
                  <Typography color="error" variant="caption" component="p" textAlign="center">
                    <FormattedMessage id="common.validation.check_for_errors" />
                  </Typography>
                )}
              </div>
            }
          />
        </form>
        {mountVerifyPhoneDialog && <VerifyPhoneDialog onClose={onCloseVerifyPhoneDialog} />}
      </Container>
    </PageBody>
  )
}
